<script>
import Logo from './../../../components/logo'
import Donate from './tabs/donate.vue'
import ViewPlans from './tabs/view-plans.vue'
import analyticsService from './../../../services/analytics-service'
import paywallTracker from '@services/mixpanel/paywall-tracker'
import {
    WALL_TYPES,
    PRESENTATION_TYPES,
    TRIGGER_REASONS,
    ENTRY_POINTS,
    CTA_TYPES,
    DISMISS_METHODS
} from '@services/mixpanel/analytics-spec'
import postAuthRedirect from '@utils/post-auth-redirect'
import premiumFunnelOrigin from '@utils/premium-funnel-origin'

export default {
    name: 'PremiumViewPlans',

    components: { Logo, Donate, ViewPlans },

    props: {
        mainComponent: {
            type: String,
            default: 'ViewPlans',
        },
    },

    computed: {
        isLoggedIn() {
            return this.$store.getters['auth/loggedIn']
        },
        plans() {
            return this.$store.getters['premium/sortedPremiumPlans']
        },
        currentSubscription() {
            return this.$store.getters['premium/activeSubscription']
        },
        isPremiumUser() {
            return this.$store.getters['premium/isPremium']
        },
        entitledFreeTrial() {
            return !this.$store.getters['premium/hasEverBeenPremium']
        },
        filteredPlans() {
            return this.plans.filter(plan => plan.active === 1)
        },
        tabs() {
            return [
                {
                    to: `/premium/view-plans`,
                    label: 'Premium',
                },
                {
                    to: `/premium/donate`,
                    label: 'Donate',
                },

            ]
        },
    },

    watch: {
        // A logged-out visitor who taps the upsell banner is routed here after
        // auth (via the post-auth redirect). If they turn out to be an existing
        // Premium subscriber, the upsell is moot — send them Home instead of the
        // plans page. Premium status resolves after login, so react to it here
        // rather than at redirect time. Scoped to banner entries so a Premium
        // member who opens the plans page deliberately still sees their plan.
        isPremiumUser: {
            immediate: true,
            handler(isPremium) {
                if (isPremium && this._isUpsellBannerEntry()) {
                    this.$router.replace(premiumFunnelOrigin.consume() || { name: 'home' })
                }
            },
        },
    },

    mounted() {
        this._trackWallShown()
    },

    methods: {
        _isUpsellBannerEntry() {
            const from = this.$route.query.from || ''
            return typeof from === 'string' &&
                (['home-banner', 'radio-banner'].includes(from) || from.startsWith('radio-channel-'))
        },
        _trackWallShown() {
            const entryPoint = this._getEntryPoint()
            paywallTracker.onWallShown({
                wallType: WALL_TYPES.VIEW_PLANS,
                presentation: PRESENTATION_TYPES.PAGE,
                triggerReason: TRIGGER_REASONS.USER_INITIATED,
                entryPoint
            })
        },
        _getEntryPoint() {
            const referrer = document.referrer || ''
            const query = this.$route.query
            if (query.from === 'topbar') return ENTRY_POINTS.TOP_BAR
            if (query.from === 'sidebar') return ENTRY_POINTS.SIDEBAR
            if (query.from === 'modal') return ENTRY_POINTS.PREMIUM_UPSELL_MODAL
            if (query.from === 'settings') return ENTRY_POINTS.SETTINGS_AUDIO_QUALITY
            if (query.from === 'account') return ENTRY_POINTS.ACCOUNT_MENU
            if (query.from === 'home-banner') return ENTRY_POINTS.HOME_BANNER
            if (query.from === 'radio-banner') return ENTRY_POINTS.RADIO_BANNER
            if (typeof query.from === 'string' && query.from.startsWith('radio-channel-')) return ENTRY_POINTS.RADIO_CHANNEL_BANNER
            if (referrer.includes('/settings')) return ENTRY_POINTS.SETTINGS_AUDIO_QUALITY
            if (referrer.includes('/account')) return ENTRY_POINTS.ACCOUNT_MENU
            return ENTRY_POINTS.DIRECT_NAVIGATION
        },
        selectPlan(plan) {
            paywallTracker.onCtaTapped(CTA_TYPES.VIEW_PLANS)
            analyticsService.premiumUpsellPlanSelected(plan.id)
            const prefix = this.$route.path.includes('offer') ? '/offer' : ''
            const from = typeof this.$route.query.from === 'string' && this.$route.query.from
                ? `?from=${encodeURIComponent(this.$route.query.from)}`
                : ''
            const target = `${prefix}/premium/select-payment/${plan.id}${from}`
            if (!this.isLoggedIn) {
                // Auth sits between plan pick and payment: stash the payment
                // page (plan id in the path, source for attribution, postAuth
                // marker for the already-Premium dissolve) and default to
                // sign-up — most signed-out pickers are new visitors.
                postAuthRedirect.set(`${target}${from ? '&' : '?'}postAuth=1`)
                this.$router.push({ name: 'signup' })
                return
            }
            this.$router.push({ path: target })
        },
        selectAmount(amount) {
            this.selectedDonationAmount = amount
        },
        handleDismiss() {
            paywallTracker.onWallDismissed(DISMISS_METHODS.CLOSE)
            this.$router.push(premiumFunnelOrigin.consume() || { name: 'home' })
        },
    },
}
</script>

<template>
    <div>
        <BaseContent v-if="tabs.length">
            <BaseSection padded>
                <Logo
                    :is-linked-to-home="false"
                    size="m"
                />
            </BaseSection>
            <BaseTabPanel
                :tabs="tabs"
                :class="$style.tabs"
                is-unpadded
                address-navigation
            />
            <BaseContent>
                <component
                    :is="mainComponent"
                    :plans="filteredPlans"
                    :current-subscription="currentSubscription"
                    :is-logged-in="isLoggedIn"
                    :is-entitled-free-trial="entitledFreeTrial"
                    @selectAmount="selectAmount"
                    @onAction="selectPlan"
                />
            </BaseContent>
            <BaseSection padded>
                <div
                    :class="$style.escapeUrl"
                    @click="handleDismiss"
                >No, thanks. I like ads and low quality audio.</div>
            </BaseSection>
        </BaseContent>
    </div>
</template>

<style lang="scss" module>
@use '@design' as *;

.escapeUrl {
    @extend %contentSubtitleM;

    margin-top: $s-xxl;
    text-align: center;
    text-decoration: underline;
    cursor: pointer;
}

@include tablet {
    .escapeUrl {
        text-align: left;
    }
}
</style>
